import { useCallback, useEffect, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import {
    loadCollectionsAsync,
    loadMoreCollectionsAsync,
} from '../../data-layer/collections/actions';
import {
    selectCollections,
    selectCollectionsCreatedLoaded,
    selectCollectionsError,
    selectCollectionsHasMore,
    selectCollectionsHasMoreSaved,
    selectCollectionsLoading,
    selectCollectionsSavedLoaded,
    selectCreatedCollections,
    selectSavedCollections,
} from '../../data-layer/collections/selectors';
import { CollectionType } from '../../data-layer/collections/service';
import { CollectionModel } from '../../data-layer/collections/types';
import { useDispatch } from '../../data-layer/useDispatch';
import { useSelector } from '../../data-layer/useSelector';

export interface UseCollectionsControllerPayload {
    collections: CollectionModel[];
    createdCollections: CollectionModel[];
    savedCollections: CollectionModel[];
    isLoading: boolean;
    isLoaded: boolean;
    isError: boolean;
    hasMore: (type?: CollectionType) => boolean;
    loadCollections: VoidFunction;
    onLoadNext: (type?: CollectionType) => void;
}

export interface UseCollectionsControllerParams {
    businessId?: string;
    namespace?: string;
    requestCondition?: boolean;
    skipSavedCollections?: boolean;
}

export const useCollectionsController = (
    params?: UseCollectionsControllerParams,
): UseCollectionsControllerPayload => {
    const dispatch = useDispatch();
    const namespace = params?.namespace || params?.businessId || 'default';
    const [hasCompletedLoad, setHasCompletedLoad] = useState(false);
    const prevLoadingRef = useRef<boolean>();

    const collections = useSelector((state) => selectCollections(state, namespace));
    const createdCollections = useSelector((state) => selectCreatedCollections(state, namespace));
    const savedCollections = useSelector((state) => selectSavedCollections(state, namespace));
    const isLoading = useSelector((state) => selectCollectionsLoading(state, namespace));
    const isError = useSelector((state) => selectCollectionsError(state, namespace));
    const hasMoreCreated = useSelector((state) => selectCollectionsHasMore(state, namespace));
    const hasMoreSaved = useSelector((state) => selectCollectionsHasMoreSaved(state, namespace));
    const createdLoaded = useSelector((state) => selectCollectionsCreatedLoaded(state, namespace));
    const savedLoaded = useSelector((state) => selectCollectionsSavedLoaded(state, namespace));

    const hasMore = useCallback(
        (type?: CollectionType) => {
            if (type === CollectionType.SAVED) {
                return hasMoreSaved;
            }

            return hasMoreCreated;
        },
        [hasMoreCreated, hasMoreSaved],
    );

    const loadCollections = useCallback(() => {
        if (!isLoading) {
            dispatch(
                loadCollectionsAsync(params?.businessId, {
                    skipSavedCollections: params?.skipSavedCollections,
                }),
            );
        }
    }, [dispatch, isLoading, params?.businessId, params?.skipSavedCollections]);

    const onLoadNext = useCallback(
        (type?: CollectionType) => {
            if (isLoading) {
                return;
            }

            dispatch(
                loadMoreCollectionsAsync({
                    businessId: params?.businessId,
                    type,
                }),
            );
        },
        [dispatch, isLoading, params?.businessId],
    );

    useEffect(() => {
        if (prevLoadingRef.current === true && !isLoading) {
            setHasCompletedLoad(true);
        }
        prevLoadingRef.current = isLoading;
    }, [isLoading]);

    const isLoaded = hasCompletedLoad || createdLoaded || savedLoaded;

    useEffectOnce(() => {
        if (typeof params?.requestCondition === 'boolean') {
            return;
        }

        const needsLoad = params?.skipSavedCollections
            ? !createdLoaded
            : !createdLoaded || !savedLoaded;

        if (needsLoad) {
            loadCollections();
        }
    });

    useEffect(() => {
        if (params?.requestCondition === true && !createdLoaded) {
            loadCollections();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params?.requestCondition, createdLoaded]);

    return {
        collections,
        createdCollections,
        savedCollections,
        isLoading,
        isLoaded,
        isError,
        hasMore,
        loadCollections,
        onLoadNext,
    };
};
